import React, { useState } from 'react';
import MoreVertical from 'lucide-react/dist/esm/icons/more-vertical';
import Download from 'lucide-react/dist/esm/icons/download';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import Eye from 'lucide-react/dist/esm/icons/eye';
import Share from 'lucide-react/dist/esm/icons/share';
import { MyFile } from '../../lib/myFilesService';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

interface FileGridProps {
  files: MyFile[];
  onFileClick: (file: MyFile) => void;
  onFileDelete: (fileId: string) => Promise<void>;
  onFileDownload: (fileId: string) => Promise<void>;
  formatFileSize: (bytes: number) => string;
  getFileIcon: (fileType: string) => string;
  onFileShare?: (file: MyFile) => void;
  onFilePreview?: (file: MyFile) => void;
}

export function FileGrid({
  files,
  onFileClick,
  onFileDelete,
  onFileDownload,
  formatFileSize,
  getFileIcon,
  onFileShare,
  onFilePreview
}: FileGridProps) {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const handleDownload = async (fileId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      setDownloading(fileId);
      await onFileDownload(fileId);
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (fileId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      return;
    }
    try {
      setDeleting(fileId);
      await onFileDelete(fileId);
    } finally {
      setDeleting(null);
    }
  };

  const handlePreview = (file: MyFile, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (onFilePreview) {
      onFilePreview(file);
    }
  };

  const handleShare = (file: MyFile, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (onFileShare) onFileShare(file);
    else alert('Share coming soon!');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      custom: '#abd3d2',
      legal: '#172241',
      financial: '#4ade80',
      health: '#ef4444',
      pets: '#f97316',
      travel: '#3b82f6',
      digital: '#8b5cf6',
      legacy: '#facc15',
      ndis: '#06b6d4'
    };
    return colors[category] || '#abd3d2';
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {files.map((file) => (
        <Card
          key={file.id}
          className="p-4 cursor-pointer hover:shadow-md transition-shadow group relative overflow-hidden"
          onClick={() => onFileClick(file)}
          onMouseEnter={() => setHoveredId(file.id)}
          onMouseLeave={() => setHoveredId(null)}
        >
          {/* File Icon */}
          <div className="mb-3 flex flex-col items-center justify-center">
            <div className="text-6xl flex justify-center mb-2 transition-transform duration-200 group-hover:scale-105">
              {getFileIcon(file.mime_type || file.file_type || '')}
            </div>
          </div>

          {/* Fade overlay with action buttons and labels, covers entire card */}
          {hoveredId === file.id && (
            <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center z-20 transition-opacity duration-200">
              <div className="w-full max-w-[220px] flex flex-col gap-2">
                <Button
                  variant="ghost"
                  className="w-full flex items-center gap-3 text-white justify-start px-4 py-2 text-base hover:bg-white/10"
                  title="Preview"
                  onClick={e => handlePreview(file, e)}
                >
                  <Eye size={22} className="mr-2" />
                  <span>Preview</span>
                </Button>
                <Button
                  variant="ghost"
                  className="w-full flex items-center gap-3 text-white justify-start px-4 py-2 text-base hover:bg-white/10"
                  title="Download"
                  onClick={e => handleDownload(file.id, e)}
                  disabled={downloading === file.id}
                >
                  <Download size={22} className="mr-2" />
                  <span>{downloading === file.id ? 'Downloading...' : 'Download'}</span>
                </Button>
                <Button
                  variant="ghost"
                  className="w-full flex items-center gap-3 text-red-200 hover:text-red-600 justify-start px-4 py-2 text-base hover:bg-white/10"
                  title="Delete"
                  onClick={e => handleDelete(file.id, e)}
                  disabled={deleting === file.id}
                >
                  <Trash2 size={22} className="mr-2" />
                  <span>{deleting === file.id ? 'Deleting...' : 'Delete'}</span>
                </Button>
                <Button
                  variant="ghost"
                  className="w-full flex items-center gap-3 text-white justify-start px-4 py-2 text-base hover:bg-white/10"
                  title="Share"
                  onClick={e => handleShare(file, e)}
                >
                  <Share size={22} className="mr-2" />
                  <span>Share</span>
                </Button>
              </div>
            </div>
          )}

          {/* File Info */}
          <div className="space-y-2">
            <div>
              <h4 className="font-medium text-gray-900 text-sm truncate" title={file.title}>
                {file.title}
              </h4>
              <p className="text-xs text-gray-500 truncate" title={file.file_name}>
                {file.file_name}
              </p>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{formatFileSize(file.file_size)}</span>
              <span>{formatDate(file.created_at)}</span>
            </div>

            {/* Category Badge */}
            <div className="flex items-center gap-2">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: getCategoryColor(file.category) }}
              />
              <span className="text-xs text-gray-600 capitalize">{file.category}</span>
            </div>

            {/* Tags */}
            {file.tags && file.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {file.tags.slice(0, 2).map((tag, index) => (
                  <span
                    key={index}
                    className="px-1 py-0.5 bg-idecide-primary bg-opacity-20 text-idecide-dark text-xs rounded"
                  >
                    {tag}
                  </span>
                ))}
                {file.tags.length > 2 && (
                  <span className="text-xs text-gray-500">+{file.tags.length - 2}</span>
                )}
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
} 